/**
 * Primary Menu functionality - Desktop dropdown navigation
 * Handles hover-based dropdowns for desktop navigation
 */
(function (global, $) {
  "use strict";

  // Prevent duplicate initialization
  if (global.primaryMenuInitialized) {
    console.log("Primary menu already initialized, skipping...");
    return;
  }

  // Use our utility functions (should be loaded before this file)
  const on =
    global.on ||
    function (element, event, handler, options) {
      const elements = Array.isArray(element) ? element : [element];
      elements.forEach((el) => {
        if (el && el.addEventListener) {
          el.addEventListener(event, handler, options);
        }
      });
      return () => {
        elements.forEach((el) => {
          if (el && el.removeEventListener) {
            el.removeEventListener(event, handler, options);
          }
        });
      };
    };

  const onDocumentReady =
    global.onDocumentReady ||
    function (callback) {
      if (document.readyState !== "loading") {
        setTimeout(callback, 0);
      } else {
        document.addEventListener("DOMContentLoaded", callback, { once: true });
      }
    };

  // Helper functions for DOM manipulation
  const addClass = (element, className) => {
    if (element) element.classList.add(className);
  };

  const removeClass = (element, className) => {
    if (element) element.classList.remove(className);
  };

  const hasClass = (element, className) => {
    return element ? element.classList.contains(className) : false;
  };

  // Detect if we're on mobile/tablet
  const isMobile = () => {
    return window.innerWidth <= 940; // Match your CSS breakpoint
  };

  // Main primary menu functionality
  const initPrimaryMenu = () => {
    // Prevent duplicate initialization
    if (global.primaryMenuInitialized) {
      console.log("Primary menu already initialized");
      return;
    }

    console.log("Initializing primary menu...");

    // Get all menu items with dropdowns
    const menuItems = document.querySelectorAll(
      ".navbar__item, .nav-item-with-dropdown, .menu-item-has-children"
    );

    if (menuItems.length === 0) {
      console.log("No primary menu items found");
      return;
    }

    // Mark as initialized
    global.primaryMenuInitialized = true;

    let hoverTimeouts = new Map(); // Track hover delays for each menu item

    // Function to close all dropdowns
    function closeAllDropdowns() {
      menuItems.forEach((item) => {
        removeClass(item, "nav-item--active");
        removeClass(item, "is-active");
      });
    }

    // Function to open specific dropdown
    function openDropdown(menuItem) {
      addClass(menuItem, "nav-item--active");
      addClass(menuItem, "is-active");
    }

    // Function to close specific dropdown
    function closeDropdown(menuItem) {
      removeClass(menuItem, "nav-item--active");
      removeClass(menuItem, "is-active");
    }

    // Setup dropdown behavior for each menu item
    menuItems.forEach((menuItem) => {
      const link = menuItem.querySelector("a");
      const dropdown = menuItem.querySelector(
        ".navbar__submenu, .dropdown, .sub-menu"
      );

      if (!link || !dropdown) return;

      // Desktop hover behavior
      on(menuItem, "mouseenter", () => {
        if (isMobile()) return;

        // Clear any existing timeout for this item
        if (hoverTimeouts.has(menuItem)) {
          clearTimeout(hoverTimeouts.get(menuItem));
        }

        // Close other dropdowns and open this one
        closeAllDropdowns();
        openDropdown(menuItem);
      });

      on(menuItem, "mouseleave", () => {
        if (isMobile()) return;

        // Add a small delay before closing to prevent flickering
        const timeout = setTimeout(() => {
          closeDropdown(menuItem);
        }, 150);
        hoverTimeouts.set(menuItem, timeout);
      });

      // Mobile click behavior (if needed for mobile navigation)
      on(link, "click", (e) => {
        if (!isMobile()) return;

        // For mobile, only handle if it's a dropdown link
        if (
          dropdown &&
          (link.getAttribute("href") === "#" ||
            link.getAttribute("href") === "")
        ) {
          e.preventDefault();

          const isCurrentlyActive = hasClass(menuItem, "nav-item--active");

          // Close all dropdowns first
          closeAllDropdowns();

          // If it wasn't active, open it
          if (!isCurrentlyActive) {
            openDropdown(menuItem);
          }
        }
      });
    });

    // Close dropdowns when clicking outside (desktop)
    const offOutsideClick = on(document, "click", (e) => {
      if (isMobile()) return;

      // Check if click is outside all menu items
      let isOutside = true;
      menuItems.forEach((menuItem) => {
        if (menuItem.contains(e.target)) {
          isOutside = false;
        }
      });

      if (isOutside) {
        closeAllDropdowns();
      }
    });

    // Close on escape key
    const offEscape = on(document, "keydown", (e) => {
      if (e.key === "Escape" && !isMobile()) {
        console.log("Escape key pressed, closing all dropdowns");
        closeAllDropdowns();
      }
    });

    // Handle window resize to switch between mobile/desktop behavior
    const offResize = on(window, "resize", () => {
      // Clear all hover timeouts on resize
      hoverTimeouts.forEach((timeout) => clearTimeout(timeout));
      hoverTimeouts.clear();

      // If switching to mobile, close all dropdowns
      if (isMobile()) {
        closeAllDropdowns();
      }
    });

    console.log("Primary menu setup complete!");

    // Enhanced keyboard navigation for accessibility
    setupKeyboardNavigation(menuItems);

    // Store cleanup functions for potential later use
    global.primaryMenuCleanup = () => {
      // Clear all timeouts
      hoverTimeouts.forEach((timeout) => clearTimeout(timeout));
      hoverTimeouts.clear();

      if (offOutsideClick) offOutsideClick();
      if (offEscape) offEscape();
      if (offResize) offResize();

      global.primaryMenuInitialized = false;
    };
  };

  // Enhanced keyboard navigation for the primary menu
  function setupKeyboardNavigation(menuItems) {
    const allLinks = Array.from(menuItems)
      .map((item) => item.querySelector("a"))
      .filter(Boolean);

    allLinks.forEach((link, index) => {
      on(link, "keydown", (e) => {
        const currentMenuItem = link.closest(
          ".navbar__item, .nav-item-with-dropdown, .menu-item-has-children"
        );

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            // Move to submenu if available
            const submenu = currentMenuItem.querySelector(
              ".navbar__submenu, .dropdown, .sub-menu"
            );
            if (submenu) {
              const firstSubmenuLink = submenu.querySelector("a");
              if (firstSubmenuLink) {
                firstSubmenuLink.focus();
                return;
              }
            }
            // Otherwise move to next main menu item
            const nextIndex = (index + 1) % allLinks.length;
            allLinks[nextIndex].focus();
            break;

          case "ArrowUp":
            e.preventDefault();
            const prevIndex = index === 0 ? allLinks.length - 1 : index - 1;
            allLinks[prevIndex].focus();
            break;

          case "ArrowRight":
            // Move to next top-level menu item
            e.preventDefault();
            if (currentMenuItem) {
              const nextMenuItem = currentMenuItem.nextElementSibling;
              if (nextMenuItem) {
                const nextLink = nextMenuItem.querySelector("a");
                if (nextLink) nextLink.focus();
              }
            }
            break;

          case "ArrowLeft":
            // Move to previous top-level menu item
            e.preventDefault();
            if (currentMenuItem) {
              const prevMenuItem = currentMenuItem.previousElementSibling;
              if (prevMenuItem) {
                const prevLink = prevMenuItem.querySelector("a");
                if (prevLink) prevLink.focus();
              }
            }
            break;

          case "Escape":
            // Close dropdown and return focus to main link
            e.preventDefault();
            if (currentMenuItem) {
              removeClass(currentMenuItem, "nav-item--active");
              removeClass(currentMenuItem, "is-active");
              link.focus();
            }
            break;

          case "Enter":
          case " ":
            // Allow default behavior for actual links
            if (
              link.getAttribute("href") &&
              link.getAttribute("href") !== "#"
            ) {
              return;
            }
            break;
        }
      });
    });
  }

  // Initialize when DOM is ready - but only once
  onDocumentReady(() => {
    if (!global.primaryMenuInitialized) {
      initPrimaryMenu();
    }
  });

  // Make initialization function available globally
  global.initPrimaryMenu = initPrimaryMenu;
})(
  typeof window !== "undefined" ? window : this,
  typeof jQuery !== "undefined" ? jQuery : undefined
);
